import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Grid, Paper, Chip, Divider,
  TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemIcon, ListItemText, Button, IconButton,
  Avatar, Card, CardContent, Alert, Stack, LinearProgress,
  Drawer, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import {
  Search, ExpandMore, ArrowBack,
  FolderOpen, CheckCircle, ViewKanban, BarChart,
  CalendarMonth, AutoAwesome, People, Chat,
  VideoCall, GetApp, Upload, Keyboard, Autorenew, AccessTime,
  HelpOutline, PlayArrow, Star, Lightbulb,
  AssignmentTurnedIn, BugReport, NotificationsActive,
  ThumbUp, ThumbDown, ContentCopy, Check,
  Menu as MenuIcon, Close,
  School, Work, Engineering,
  NewReleases, ErrorOutline, MenuBook,
  Slideshow, Link as LinkIcon,
  BoltOutlined, CallSplit, History, Add, TrendingUp, Group,
  Settings as SettingsDocIcon,
  FolderSpecial as PortfolioIcon,
  Tune as TuneDocIcon,
  Terminal, Security, Code
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// ─── Données statiques ────────────────────────────────────────────────────────

const SECTION_IDS = [
  { id: 'quickstart',    icon: <PlayArrow /> },
  { id: 'demos',         icon: <Slideshow /> },
  { id: 'profiles',      icon: <People /> },
  { id: 'projects',      icon: <FolderOpen /> },
  { id: 'tasks',         icon: <AssignmentTurnedIn /> },
  { id: 'customfields',  icon: <TuneDocIcon /> },
  { id: 'views',         icon: <ViewKanban /> },
  { id: 'portfolio',     icon: <PortfolioIcon /> },
  { id: 'agile',         icon: <Autorenew /> },
  { id: 'timetracking',  icon: <AccessTime /> },
  { id: 'automation',    icon: <BoltOutlined /> },
  { id: 'workload',      icon: <TrendingUp /> },
  { id: 'ai',            icon: <AutoAwesome /> },
  { id: 'collaboration', icon: <Chat /> },
  { id: 'importExport',  icon: <GetApp /> },
  { id: 'errors',        icon: <ErrorOutline /> },
  { id: 'glossary',      icon: <MenuBook /> },
  { id: 'shortcuts',     icon: <Keyboard /> },
  { id: 'changelog',     icon: <NewReleases /> },
  { id: 'claude',        icon: <Terminal /> },
  { id: 'faq',           icon: <HelpOutline /> },
];

const DEMOS = [
  { color: '#3b82f6', icon: <AssignmentTurnedIn sx={{ fontSize: 44 }} />, tk: 'demoTask',   helpSubject: 'Création de tâche' },
  { color: '#8b5cf6', icon: <BarChart sx={{ fontSize: 44 }} />,           tk: 'demoGantt',  helpSubject: 'Vue Gantt' },
  { color: '#f59e0b', icon: <AutoAwesome sx={{ fontSize: 44 }} />,        tk: 'demoAi',     helpSubject: 'Assistant IA' },
  { color: '#22c55e', icon: <People sx={{ fontSize: 44 }} />,             tk: 'demoInvite', helpSubject: 'Invitation membre' },
];

const PROFILES = [
  {
    tk: 'profileStudent',
    icon: <School />,
    color: 'info',
    features: ['profileStudentF1', 'profileStudentF2', 'profileStudentF3', 'profileStudentF4', 'profileStudentF5'],
  },
  {
    tk: 'profileFreelance',
    icon: <Work />,
    color: 'success',
    features: ['profileFreelanceF1', 'profileFreelanceF2', 'profileFreelanceF3', 'profileFreelanceF4', 'profileFreelanceF5'],
  },
  {
    tk: 'profileManager',
    icon: <Engineering />,
    color: 'warning',
    features: ['profileManagerF1', 'profileManagerF2', 'profileManagerF3', 'profileManagerF4', 'profileManagerF5'],
  },
];

const ERRORS = [
  { icon: <Upload />,              tk: 'errorCsv',    subject: 'Import CSV' },
  { icon: <People />,              tk: 'errorInvite', subject: 'Invitation expirée' },
  { icon: <BarChart />,            tk: 'errorGantt',  subject: 'Gantt vide' },
  { icon: <AutoAwesome />,         tk: 'errorAi',     subject: "L'IA ne répond pas" },
  { icon: <NotificationsActive />, tk: 'errorNotif',  subject: 'Notifications manquantes' },
];

const GLOSSARY = [
  { tk: 'glossPhase' },
  { tk: 'glossDep' },
  { tk: 'glossPin' },
  { tk: 'glossGantt' },
  { tk: 'glossKanban' },
  { tk: 'glossMilestone' },
  { tk: 'glossSprint' },
  { tk: 'glossBacklog' },
  { tk: 'glossPortfolio' },
  { tk: 'glossCustomField' },
];

const SHORTCUTS = [
  { keys: ['N'],           tk: 'shortcutNewProject' },
  { keys: ['T'],           tk: 'shortcutNewTask' },
  { keys: ['G'],           tk: 'shortcutGantt' },
  { keys: ['K'],           tk: 'shortcutKanban' },
  { keys: ['C'],           tk: 'shortcutCalendar' },
  { keys: ['Ctrl', 'F'],   tk: 'shortcutSearch' },
  { keys: ['Ctrl', 'S'],   tk: 'shortcutSave' },
  { keys: ['Échap / Esc'], tk: 'shortcutClose' },
  { keys: ['?'],           tk: 'shortcutHelp' },
];

const CHANGELOG = [
  { version: 'v3.2.0', date: 'Août 2026',     badge: true,  tk: 'cl320' },
  { version: 'v3.1.0', date: 'Avril 2026',    badge: false, tk: 'cl310' },
  { version: 'v3.0.0', date: 'Avril 2026',    badge: false, tk: 'cl300' },
  { version: 'v2.9.0', date: 'Avril 2026',    badge: false, tk: 'cl290' },
  { version: 'v2.8.0', date: 'Avril 2026',    badge: false, tk: 'cl280' },
  { version: 'v2.7.0', date: 'Avril 2026',    badge: false, tk: 'cl270' },
  { version: 'v2.6.0', date: 'Avril 2026',    badge: false, tk: 'cl260' },
  { version: 'v2.5.0', date: 'Avril 2025',    badge: false, tk: 'cl250' },
  { version: 'v2.4.0', date: 'Mars 2025',     badge: false, tk: 'cl240' },
  { version: 'v2.3.0', date: 'Février 2025',  badge: false, tk: 'cl230' },
  { version: 'v2.2.0', date: 'Janvier 2025',  badge: false, tk: 'cl220' },
  { version: 'v2.1.0', date: 'Décembre 2024', badge: false, tk: 'cl210' },
];

const FAQ_KEYS = [
  'faqInvite', 'faqOffline', 'faqExportPdf', 'faqSubtask',
  'faqAi', 'faqAiActions',
  'faqTimeTimer', 'faqTimeManual', 'faqTimeExport',
  'faqSprintCreate', 'faqSprintPoints', 'faqSprintEnd',
  'faqAutoCreate', 'faqAutoTriggers', 'faqAutoActions',
  'faqWorkloadCap', 'faqWorkloadAI', 'faqWorkloadStatus',
  'faqPortfolio', 'faqCustomFields', 'faqCustomFieldsFilter',
  'faqMilestone', 'faqRole', 'faqArchive', 'faqLanguage', 'faqBlocked', 'faqFree',
  'faqMcpSetup', 'faqMcpTools', 'faqMcpError',
];

// ─── Composants utilitaires ───────────────────────────────────────────────────

function SectionTitle({ id, icon, children, onCopy, copied }) {
  return (
    <Box
      id={id}
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 5, mb: 2.5, scrollMarginTop: '80px' }}
    >
      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, flexShrink: 0 }}>{icon}</Avatar>
      <Typography variant="h5" fontWeight="bold" sx={{ flex: 1 }}>{children}</Typography>
      <Tooltip title={copied ? 'Lien copié !' : 'Copier le lien'} placement="top">
        <IconButton size="small" onClick={() => onCopy(id)} color={copied ? 'success' : 'default'}>
          {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function StepCard({ number, title, description }) {
  return (
    <Card elevation={1} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.9rem', fontWeight: 'bold' }}>
            {number}
          </Avatar>
          <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </CardContent>
    </Card>
  );
}

function KeyBadge({ children }) {
  const theme = useTheme();
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      px: 1, py: 0.25, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem',
      fontWeight: 'bold',
      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
      border: '1px solid', borderColor: 'divider', mx: 0.25,
      boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 0 rgba(0,0,0,0.2)',
    }}>
      {children}
    </Box>
  );
}

function FeatureRow({ icon, title, description }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
      <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 40, height: 40, mt: 0.25, flexShrink: 0 }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </Box>
    </Box>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function DocumentationPage() {
  const navigate  = useNavigate();
  const { t, i18n } = useTranslation();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));
  const isRtl     = i18n.language === 'ar';

  const [readProgress,   setReadProgress]   = useState(0);
  const [activeSection,  setActiveSection]  = useState('quickstart');
  const [search,         setSearch]         = useState('');
  const [mobileNavOpen,  setMobileNavOpen]  = useState(false);
  const [copiedId,       setCopiedId]       = useState(null);
  const [faqRatings,     setFaqRatings]     = useState(() => {
    try { return JSON.parse(localStorage.getItem('era_faq_ratings') || '{}'); }
    catch { return {}; }
  });

  // ── Barre de progression + section active au scroll ──
  useEffect(() => {
    const onScroll = () => {
      const doc   = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setReadProgress(total > 0 ? (doc.scrollTop / total) * 100 : 0);

      let current = SECTION_IDS[0].id;
      for (const { id } of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Ancre initiale depuis l'URL (#section) ──
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && SECTION_IDS.some(s => s.id === hash)) {
      setTimeout(() => scrollTo(hash), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    setMobileNavOpen(false);
    window.history.replaceState(null, '', `#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/documentation#${id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const rateFaq = (key, value) => {
    const updated = { ...faqRatings, [key]: value };
    setFaqRatings(updated);
    localStorage.setItem('era_faq_ratings', JSON.stringify(updated));
  };

  // ── Index de recherche global ──────────────────────────────────────────────
  const searchIndex = useMemo(() => {
    const items = [];
    const add = (sectionId, text) => items.push({ sectionId, text: text.toLowerCase() });

    SECTION_IDS.forEach(({ id }) => add(id, t(`documentation.section_${id}`)));

    // Quickstart
    [1,2,3,4].forEach(n => add('quickstart', t(`documentation.step${n}Title`) + ' ' + t(`documentation.step${n}Desc`)));
    // Projets
    ['projCreate','projPin','projArchive','projRoles'].forEach(k => add('projects', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Tâches
    ['taskCreate','taskSubtask','taskDeps','taskNotif'].forEach(k => add('tasks', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Champs perso
    ['cfTypes','cfCreate','cfFill','cfFilter'].forEach(k => add('customfields', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Agile
    ['agileBacklog','agileSprintCreate','agileStoryPoints','agileBurndown'].forEach(k => add('agile', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Temps
    ['timetrackingTimer','timetrackingManual','timetrackingReport','timetrackingExport'].forEach(k => add('timetracking', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Automatisations
    ['automationTrigger','automationAction','automationCreate','automationLog'].forEach(k => add('automation', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Workload
    ['workloadOverview','workloadCapacity','workloadAI','workloadTasks'].forEach(k => add('workload', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // IA
    ['aiPlanning','aiPriority','aiAssistant','aiActions','aiEstimation'].forEach(k => add('ai', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Collaboration
    ['colTeam','colChat','colTaskChat','colMeetings'].forEach(k => add('collaboration', t(`documentation.${k}`) + ' ' + t(`documentation.${k}Desc`)));
    // Claude
    [1,2,3,4].forEach(n => add('claude', t(`documentation.claudeStep${n}Title`) + ' ' + t(`documentation.claudeStep${n}Desc`)));
    ['claudeIntro','claudeToolsDesc','claudeSecurityDesc'].forEach(k => add('claude', t(`documentation.${k}`)));
    add('claude', 'mcp model context protocol token api');
    // Glossaire
    GLOSSARY.forEach(({ tk }) => add('glossary', t(`documentation.${tk}Term`) + ' ' + t(`documentation.${tk}Def`)));
    // Erreurs
    ERRORS.forEach(({ tk }) => add('errors', t(`documentation.${tk}Title`) + ' ' + t(`documentation.${tk}Desc`) + ' ' + t(`documentation.${tk}Fix`)));
    // FAQ
    FAQ_KEYS.forEach(key => add('faq', t(`documentation.${key}Q`) + ' ' + t(`documentation.${key}A`)));

    return items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // ── Résultats de recherche globaux ─────────────────────────────────────────
  const globalSearchResults = useMemo(() => {
    if (!search || search.length < 2) return [];
    const s = search.toLowerCase();
    const matched = new Set();
    searchIndex.forEach(({ sectionId, text }) => {
      if (text.includes(s)) matched.add(sectionId);
    });
    return SECTION_IDS.filter(({ id }) => matched.has(id));
  }, [searchIndex, search]);

  const filteredFaq = FAQ_KEYS.filter(key => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t(`documentation.${key}Q`).toLowerCase().includes(s)
        || t(`documentation.${key}A`).toLowerCase().includes(s);
  });

  // ── Sidebar (réutilisé dans desktop et drawer mobile) ──
  const SidebarContent = () => (
    <Box sx={{ p: 2 }}>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">{t('documentation.toc')}</Typography>
          <IconButton onClick={() => setMobileNavOpen(false)}><Close /></IconButton>
        </Box>
      )}
      {!isMobile && (
        <Typography variant="overline" color="text.secondary" fontWeight="bold" sx={{ px: 1 }}>
          {t('documentation.toc')}
        </Typography>
      )}
      <List dense disablePadding sx={{ mt: 1 }}>
        {SECTION_IDS.map(({ id, icon }) => (
          <ListItem
            key={id}
            button
            onClick={() => scrollTo(id)}
            selected={activeSection === id}
            sx={{
              borderRadius: 1.5, mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main', color: 'primary.contrastText',
                '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: activeSection === id ? 'inherit' : 'primary.main' }}>
              {icon}
            </ListItemIcon>
            <ListItemText
              primary={t(`documentation.section_${id}`)}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: activeSection === id ? 'bold' : 'normal' }}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Button fullWidth variant="outlined" size="small" startIcon={<BugReport />} onClick={() => navigate('/helpdesk')}>
        {t('documentation.reportIssue')}
      </Button>
    </Box>
  );

  return (
    <>
      {/* ── Barre de progression de lecture (feature #4) ── */}
      <LinearProgress
        variant="determinate"
        value={readProgress}
        sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 3, borderRadius: 0 }}
      />

      <Container maxWidth="xl" sx={{ py: 4 }} dir={isRtl ? 'rtl' : 'ltr'}>

        {/* ── En-tête ── */}
        <Box sx={{ mb: 4 }}>
          <Button startIcon={<ArrowBack sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
            onClick={() => navigate(-1)} sx={{ mb: 2 }} size="small">
            {t('documentation.back')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 52, height: 52 }}><HelpOutline /></Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold">{t('documentation.pageTitle')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('documentation.pageSubtitle')}</Typography>
            </Box>
          </Box>
          <Alert severity="info" icon={<Lightbulb />} sx={{ mt: 2 }}>
            {t('documentation.needHelp')}{' '}
            <Button size="small" onClick={() => navigate('/helpdesk')} sx={{ ml: 1 }}>
              {t('documentation.contactSupport')}
            </Button>
          </Alert>
        </Box>

        {/* ── Recherche ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
          <TextField
            fullWidth
            placeholder={t('documentation.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><Close fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {/* Bouton nav mobile (feature #8) */}
          {isMobile && (
            <Button variant="outlined" onClick={() => setMobileNavOpen(true)} startIcon={<MenuIcon />} sx={{ flexShrink: 0 }}>
              {t('documentation.toc')}
            </Button>
          )}
        </Box>

        {/* ── Résultats de recherche globaux ── */}
        {search.length >= 2 && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: globalSearchResults.length > 0 ? 'primary.light' : 'divider', borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Search fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                {globalSearchResults.length > 0
                  ? t('documentation.searchResultsTitle') + ' — ' + t('documentation.searchResultsCount', { count: globalSearchResults.length })
                  : t('documentation.searchNoResults', { query: search })}
              </Typography>
              {globalSearchResults.length === 0 && (
                <Button size="small" onClick={() => navigate('/helpdesk')}>{t('documentation.contactSupport')}</Button>
              )}
            </Box>
            {globalSearchResults.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5 }}>
                {globalSearchResults.map(({ id, icon }) => (
                  <Chip
                    key={id}
                    icon={icon}
                    label={t(`documentation.section_${id}`)}
                    onClick={() => scrollTo(id)}
                    variant="outlined"
                    color="primary"
                    size="small"
                    clickable
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            )}
          </Paper>
        )}

        {/* ── Drawer navigation mobile (feature #8) ── */}
        <Drawer
          anchor={isRtl ? 'right' : 'left'}
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          PaperProps={{ sx: { width: 280 } }}
        >
          <SidebarContent />
        </Drawer>

        <Grid container spacing={3}>

          {/* ── Sidebar desktop ── */}
          {!isMobile && (
            <Grid item md={3}>
              <Paper elevation={0} sx={{ position: 'sticky', top: 80, border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
                <SidebarContent />
              </Paper>
            </Grid>
          )}

          {/* ── Contenu ── */}
          <Grid item xs={12} md={9}>

            {/* ══════════════════════════════════════
                1. DÉMARRAGE RAPIDE
            ══════════════════════════════════════ */}
            <SectionTitle id="quickstart" icon={<PlayArrow />} onCopy={copyLink} copied={copiedId === 'quickstart'}>
              {t('documentation.section_quickstart')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.quickstartIntro')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[1, 2, 3, 4].map(n => (
                <Grid item xs={12} sm={6} key={n}>
                  <StepCard number={n} title={t(`documentation.step${n}Title`)} description={t(`documentation.step${n}Desc`)} />
                </Grid>
              ))}
            </Grid>
            <Alert severity="success" icon={<AutoAwesome />} sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong> {t('documentation.quickstartTip')}
            </Alert>

            {/* ══════════════════════════════════════
                2. DÉMONSTRATIONS (feature #1)
            ══════════════════════════════════════ */}
            <SectionTitle id="demos" icon={<Slideshow />} onCopy={copyLink} copied={copiedId === 'demos'}>
              {t('documentation.section_demos')}
            </SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.demosIntro')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {DEMOS.map(({ color, icon, tk, helpSubject }) => (
                <Grid item xs={12} sm={6} key={tk}>
                  <Card elevation={1} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    {/* Zone de démo / placeholder GIF */}
                    <Box sx={{
                      height: 160, position: 'relative',
                      background: `linear-gradient(135deg, ${color}18, ${color}35)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderBottom: '1px solid', borderColor: 'divider',
                    }}>
                      <Avatar sx={{ bgcolor: color + '25', color: color, width: 72, height: 72 }}>
                        {icon}
                      </Avatar>
                      <Chip
                        label={t('documentation.demoComingSoon')}
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: color, color: '#fff', fontWeight: 'bold' }}
                      />
                    </Box>
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Typography variant="subtitle2" fontWeight="bold">{t(`documentation.${tk}Title`)}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t(`documentation.${tk}Desc`)}
                      </Typography>
                      <Button
                        size="small" variant="text" sx={{ mt: 1, px: 0 }}
                        onClick={() => navigate('/helpdesk', { state: { prefill: { subject: helpSubject, department: 'general' } } })}
                      >
                        {t('documentation.demoHelp')} →
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* ══════════════════════════════════════
                3. GUIDES PAR PROFIL (feature #3)
            ══════════════════════════════════════ */}
            <SectionTitle id="profiles" icon={<People />} onCopy={copyLink} copied={copiedId === 'profiles'}>
              {t('documentation.section_profiles')}
            </SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.profilesIntro')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {PROFILES.map(({ tk, icon, color, features }) => (
                <Grid item xs={12} sm={4} key={tk}>
                  <Card elevation={1} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ p: 2, bgcolor: `${color}.main`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}>{icon}</Avatar>
                      <Typography variant="subtitle1" fontWeight="bold" color="#fff">
                        {t(`documentation.${tk}Title`)}
                      </Typography>
                    </Box>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {t(`documentation.${tk}Desc`)}
                      </Typography>
                      <List dense disablePadding>
                        {features.map(fk => (
                          <ListItem key={fk} sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <CheckCircle sx={{ fontSize: 14 }} color="success" />
                            </ListItemIcon>
                            <ListItemText primary={t(`documentation.${fk}`)} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                      <Button size="small" variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => navigate('/dashboard')}>
                        {t('documentation.profileStart')}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* ══════════════════════════════════════
                4. PROJETS
            ══════════════════════════════════════ */}
            <SectionTitle id="projects" icon={<FolderOpen />} onCopy={copyLink} copied={copiedId === 'projects'}>
              {t('documentation.section_projects')}
            </SectionTitle>
            <FeatureRow icon={<Star />}       title={t('documentation.projCreate')}  description={t('documentation.projCreateDesc')} />
            <FeatureRow icon={<Star />}       title={t('documentation.projPin')}     description={t('documentation.projPinDesc')} />
            <FeatureRow icon={<FolderOpen />} title={t('documentation.projArchive')} description={t('documentation.projArchiveDesc')} />
            <FeatureRow icon={<People />}     title={t('documentation.projRoles')}   description={t('documentation.projRolesDesc')} />
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('documentation.projStatuses')}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {['statusActive','statusOnHold','statusCompleted','statusCancelled','statusArchived'].map(k => (
                  <Chip key={k} label={t(`documentation.${k}`)} size="small" variant="outlined" />
                ))}
              </Stack>
            </Paper>

            {/* ══════════════════════════════════════
                5. TÂCHES
            ══════════════════════════════════════ */}
            <SectionTitle id="tasks" icon={<AssignmentTurnedIn />} onCopy={copyLink} copied={copiedId === 'tasks'}>
              {t('documentation.section_tasks')}
            </SectionTitle>
            <FeatureRow icon={<CheckCircle />}         title={t('documentation.taskCreate')}  description={t('documentation.taskCreateDesc')} />
            <FeatureRow icon={<AssignmentTurnedIn />}  title={t('documentation.taskSubtask')} description={t('documentation.taskSubtaskDesc')} />
            <FeatureRow icon={<BarChart />}            title={t('documentation.taskDeps')}    description={t('documentation.taskDepsDesc')} />
            <FeatureRow icon={<NotificationsActive />} title={t('documentation.taskNotif')}   description={t('documentation.taskNotifDesc')} />
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('documentation.taskPriorities')}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={t('documentation.priorityLow')}    size="small" color="success" />
                <Chip label={t('documentation.priorityMedium')} size="small" color="info" />
                <Chip label={t('documentation.priorityHigh')}   size="small" color="warning" />
                <Chip label={t('documentation.priorityUrgent')} size="small" color="error" />
              </Stack>
            </Paper>

            {/* ══════════════════════════════════════
                5b. CHAMPS PERSONNALISÉS
            ══════════════════════════════════════ */}
            <SectionTitle id="customfields" icon={<TuneDocIcon />} onCopy={copyLink} copied={copiedId === 'customfields'}>
              {t('documentation.section_customfields')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.customfieldsIntro')}
            </Typography>
            <FeatureRow icon={<TuneDocIcon />}        title={t('documentation.cfTypes')}  description={t('documentation.cfTypesDesc')} />
            <FeatureRow icon={<Add />}                title={t('documentation.cfCreate')} description={t('documentation.cfCreateDesc')} />
            <FeatureRow icon={<AssignmentTurnedIn />} title={t('documentation.cfFill')}   description={t('documentation.cfFillDesc')} />
            <FeatureRow icon={<Search />}             title={t('documentation.cfFilter')} description={t('documentation.cfFilterDesc')} />
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('documentation.cfTypesTitle')}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {['cfTypeText','cfTypeNumber','cfTypeDate','cfTypeSelect','cfTypeCheckbox'].map(k => (
                  <Chip key={k} label={t(`documentation.${k}`)} size="small" variant="outlined" />
                ))}
              </Stack>
            </Paper>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong>{' '}{t('documentation.cfTip')}
            </Alert>

            {/* ══════════════════════════════════════
                6. VUES
            ══════════════════════════════════════ */}
            <SectionTitle id="views" icon={<ViewKanban />} onCopy={copyLink} copied={copiedId === 'views'}>
              {t('documentation.section_views')}
            </SectionTitle>
            <FeatureRow icon={<BarChart />}           title={t('documentation.viewGantt')}    description={t('documentation.viewGanttDesc')} />
            <FeatureRow icon={<ViewKanban />}         title={t('documentation.viewKanban')}   description={t('documentation.viewKanbanDesc')} />
            <FeatureRow icon={<CalendarMonth />}      title={t('documentation.viewCalendar')} description={t('documentation.viewCalendarDesc')} />
            <FeatureRow icon={<AssignmentTurnedIn />} title={t('documentation.viewMyCards')}  description={t('documentation.viewMyCardsDesc')} />

            {/* ══════════════════════════════════════
                6b. VUE PORTFOLIO
            ══════════════════════════════════════ */}
            <SectionTitle id="portfolio" icon={<PortfolioIcon />} onCopy={copyLink} copied={copiedId === 'portfolio'}>
              {t('documentation.section_portfolio')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.portfolioIntro')}
            </Typography>
            <FeatureRow icon={<BarChart />}    title={t('documentation.portfolioKPIs')}      description={t('documentation.portfolioKPIsDesc')} />
            <FeatureRow icon={<CheckCircle />} title={t('documentation.portfolioHealth')}    description={t('documentation.portfolioHealthDesc')} />
            <FeatureRow icon={<AccessTime />}  title={t('documentation.portfolioTimeline')}  description={t('documentation.portfolioTimelineDesc')} />
            <FeatureRow icon={<BarChart />}    title={t('documentation.portfolioCharts')}    description={t('documentation.portfolioChartsDesc')} />
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('documentation.portfolioHealthLegend')}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={t('documentation.portfolioGreen')}  size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} />
                <Chip label={t('documentation.portfolioOrange')} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 'bold' }} />
                <Chip label={t('documentation.portfolioRed')}    size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 'bold' }} />
              </Stack>
            </Paper>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong>{' '}{t('documentation.portfolioTip')}
            </Alert>

            {/* ══════════════════════════════════════
                6c. GESTION AGILE / SPRINTS
            ══════════════════════════════════════ */}
            <SectionTitle id="agile" icon={<Autorenew />} onCopy={copyLink} copied={copiedId === 'agile'}>
              {t('documentation.section_agile')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.agileIntro')}
            </Typography>
            <FeatureRow icon={<AssignmentTurnedIn />} title={t('documentation.agileBacklog')}      description={t('documentation.agileBacklogDesc')} />
            <FeatureRow icon={<Autorenew />}          title={t('documentation.agileSprintCreate')} description={t('documentation.agileSprintCreateDesc')} />
            <FeatureRow icon={<Star />}               title={t('documentation.agileStoryPoints')}  description={t('documentation.agileStoryPointsDesc')} />
            <FeatureRow icon={<BarChart />}           title={t('documentation.agileBurndown')}     description={t('documentation.agileBurndownDesc')} />
            <FeatureRow icon={<BarChart />}           title={t('documentation.agileVelocity')}     description={t('documentation.agileVelocityDesc')} />
            <FeatureRow icon={<CheckCircle />}        title={t('documentation.agileSprintEnd')}    description={t('documentation.agileSprintEndDesc')} />
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong>{' '}
              {i18n.language === 'fr'
                ? "L'onglet Sprints est accessible depuis la page de détail d'un projet, à côté des onglets Liste, Kanban et Gantt."
                : i18n.language === 'ar'
                ? 'يمكن الوصول إلى تبويب السبرينت من صفحة تفاصيل المشروع، بجانب تبويبات القائمة وكانبان وغانت.'
                : 'The Sprints tab is accessible from the project detail page, next to the List, Kanban, and Gantt tabs.'}
            </Alert>

            {/* ══════════════════════════════════════
                6c. SUIVI DU TEMPS
            ══════════════════════════════════════ */}
            <SectionTitle id="timetracking" icon={<AccessTime />} onCopy={copyLink} copied={copiedId === 'timetracking'}>
              {t('documentation.section_timetracking')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.timetrackingIntro')}
            </Typography>
            <FeatureRow icon={<AccessTime />}         title={t('documentation.timetrackingTimer')}   description={t('documentation.timetrackingTimerDesc')} />
            <FeatureRow icon={<Star />}               title={t('documentation.timetrackingManual')}  description={t('documentation.timetrackingManualDesc')} />
            <FeatureRow icon={<BarChart />}           title={t('documentation.timetrackingReport')}  description={t('documentation.timetrackingReportDesc')} />
            <FeatureRow icon={<GetApp />}             title={t('documentation.timetrackingExport')}  description={t('documentation.timetrackingExportDesc')} />
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong>{' '}
              {i18n.language === 'fr'
                ? 'Un seul chronomètre peut être actif à la fois par utilisateur. Démarrer un nouveau chrono arrête automatiquement le précédent.'
                : i18n.language === 'ar'
                ? 'لا يمكن تشغيل أكثر من مؤقت واحد في نفس الوقت لكل مستخدم. يؤدي بدء مؤقت جديد إلى إيقاف السابق تلقائياً.'
                : 'Only one timer can be active at a time per user. Starting a new timer automatically stops the previous one.'}
            </Alert>

            {/* ══════════════════════════════════════
                7. AUTOMATISATIONS
            ══════════════════════════════════════ */}
            <SectionTitle id="automation" icon={<BoltOutlined />} onCopy={copyLink} copied={copiedId === 'automation'}>
              {t('documentation.section_automation')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.automationIntro')}
            </Typography>
            <FeatureRow icon={<BoltOutlined />}        title={t('documentation.automationTrigger')} description={t('documentation.automationTriggerDesc')} />
            <FeatureRow icon={<CallSplit />}           title={t('documentation.automationAction')}  description={t('documentation.automationActionDesc')} />
            <FeatureRow icon={<Add />}                 title={t('documentation.automationCreate')}  description={t('documentation.automationCreateDesc')} />
            <FeatureRow icon={<History />}             title={t('documentation.automationLog')}     description={t('documentation.automationLogDesc')} />
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong>{' '}
              {i18n.language === 'fr'
                ? 'Les automatisations sont exécutées en arrière-plan sans bloquer votre interface. Utilisez l\'historique pour vérifier qu\'elles fonctionnent comme prévu.'
                : i18n.language === 'ar'
                ? 'تُنفَّذ الأتمتة في الخلفية دون إعاقة الواجهة. استخدم السجل للتحقق من أنها تعمل كما هو متوقع.'
                : 'Automations run in the background without blocking your interface. Use the history to verify they work as expected.'}
            </Alert>

            {/* ══════════════════════════════════════
                8. CHARGE DE TRAVAIL
            ══════════════════════════════════════ */}
            <SectionTitle id="workload" icon={<TrendingUp />} onCopy={copyLink} copied={copiedId === 'workload'}>
              {t('documentation.section_workload')}
            </SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.workloadIntro')}
            </Typography>
            <FeatureRow icon={<Group />}       title={t('documentation.workloadOverview')}  description={t('documentation.workloadOverviewDesc')} />
            <FeatureRow icon={<SettingsDocIcon />} title={t('documentation.workloadCapacity')}  description={t('documentation.workloadCapacityDesc')} />
            <FeatureRow icon={<AutoAwesome />} title={t('documentation.workloadAI')}         description={t('documentation.workloadAIDesc')} />
            <FeatureRow icon={<BarChart />}    title={t('documentation.workloadTasks')}      description={t('documentation.workloadTasksDesc')} />
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{t('documentation.tip')} :</strong>{' '}
              {i18n.language === 'fr'
                ? 'Le rééquilibrage IA tient compte des compétences supposées et de la priorité des tâches. Si l\'IA n\'est pas configurée, un algorithme de secours analyse les écarts et propose des déplacements de tâches non démarrées.'
                : i18n.language === 'ar'
                ? 'تأخذ إعادة التوازن بالذكاء الاصطناعي في الاعتبار المهارات المفترضة وأولوية المهام. إذا لم يكن الذكاء الاصطناعي متاحاً، يحلل خوارزم احتياطي الفجوات ويقترح نقل المهام غير المبدوءة.'
                : 'AI rebalancing considers assumed skills and task priorities. If AI is not configured, a fallback algorithm analyzes gaps and suggests moving not-started tasks.'}
            </Alert>

            {/* ══════════════════════════════════════
                9. ASSISTANT IA
            ══════════════════════════════════════ */}
            <SectionTitle id="ai" icon={<AutoAwesome />} onCopy={copyLink} copied={copiedId === 'ai'}>
              {t('documentation.section_ai')}
            </SectionTitle>
            <Alert severity="info" sx={{ mb: 2 }}>{t('documentation.aiInfo')}</Alert>
            <FeatureRow icon={<AutoAwesome />}         title={t('documentation.aiPlanning')}   description={t('documentation.aiPlanningDesc')} />
            <FeatureRow icon={<Star />}                title={t('documentation.aiPriority')}   description={t('documentation.aiPriorityDesc')} />
            <FeatureRow icon={<Chat />}                title={t('documentation.aiAssistant')}  description={t('documentation.aiAssistantDesc')} />
            <FeatureRow icon={<AssignmentTurnedIn />}  title={t('documentation.aiActions')}    description={t('documentation.aiActionsDesc')} />
            <FeatureRow icon={<BarChart />}            title={t('documentation.aiEstimation')} description={t('documentation.aiEstimationDesc')} />

            {/* ══════════════════════════════════════
                8. COLLABORATION
            ══════════════════════════════════════ */}
            <SectionTitle id="collaboration" icon={<Chat />} onCopy={copyLink} copied={copiedId === 'collaboration'}>
              {t('documentation.section_collaboration')}
            </SectionTitle>
            <FeatureRow icon={<People />}    title={t('documentation.colTeam')}     description={t('documentation.colTeamDesc')} />
            <FeatureRow icon={<Chat />}      title={t('documentation.colChat')}     description={t('documentation.colChatDesc')} />
            <FeatureRow icon={<Chat />}      title={t('documentation.colTaskChat')} description={t('documentation.colTaskChatDesc')} />
            <FeatureRow icon={<VideoCall />} title={t('documentation.colMeetings')} description={t('documentation.colMeetingsDesc')} />

            {/* ══════════════════════════════════════
                9. IMPORT / EXPORT
            ══════════════════════════════════════ */}
            <SectionTitle id="importExport" icon={<GetApp />} onCopy={copyLink} copied={copiedId === 'importExport'}>
              {t('documentation.section_importExport')}
            </SectionTitle>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Card elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Upload color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{t('documentation.importTitle')}</Typography>
                    </Box>
                    <List dense disablePadding>
                      {['importCsv','importJson','importTemplate'].map(k => (
                        <ListItem key={k} sx={{ px: 0, py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}><CheckCircle sx={{ fontSize: 14 }} color="success" /></ListItemIcon>
                          <ListItemText primary={t(`documentation.${k}`)} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <GetApp color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{t('documentation.exportTitle')}</Typography>
                    </Box>
                    <List dense disablePadding>
                      {['exportCsv','exportJson','exportPdf'].map(k => (
                        <ListItem key={k} sx={{ px: 0, py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}><CheckCircle sx={{ fontSize: 14 }} color="success" /></ListItemIcon>
                          <ListItemText primary={t(`documentation.${k}`)} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('documentation.importWarning')}
            </Alert>

            {/* ══════════════════════════════════════
                10. ERREURS FRÉQUENTES (feature #9)
            ══════════════════════════════════════ */}
            <SectionTitle id="errors" icon={<ErrorOutline />} onCopy={copyLink} copied={copiedId === 'errors'}>
              {t('documentation.section_errors')}
            </SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.errorsIntro')}
            </Typography>
            {ERRORS.map(({ icon, tk, subject }) => (
              <Card key={tk} elevation={0} sx={{ border: '1px solid', borderColor: 'error.light', mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'error.light', color: 'error.main', flexShrink: 0 }}>{icon}</Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                        {t(`documentation.${tk}Title`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t(`documentation.${tk}Desc`)}
                      </Typography>
                      <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 1.5, py: 0.5 }}>
                        <Typography variant="body2">
                          <strong>{t('documentation.solution')} : </strong>
                          {t(`documentation.${tk}Fix`)}
                        </Typography>
                      </Alert>
                      {/* Lien HelpDesk pré-rempli (feature #11) */}
                      <Button
                        size="small" variant="text" color="error" sx={{ mt: 1, px: 0 }}
                        onClick={() => navigate('/helpdesk', {
                          state: { prefill: { subject: subject, department: 'technical', priority: 'high' } }
                        })}
                      >
                        {t('documentation.errorContactBtn')} →
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {/* ══════════════════════════════════════
                11. GLOSSAIRE (feature #10)
            ══════════════════════════════════════ */}
            <SectionTitle id="glossary" icon={<MenuBook />} onCopy={copyLink} copied={copiedId === 'glossary'}>
              {t('documentation.section_glossary')}
            </SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.glossaryIntro')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {GLOSSARY.map(({ tk }) => (
                <Grid item xs={12} sm={6} key={tk}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                      {t(`documentation.${tk}Term`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`documentation.${tk}Def`)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* ══════════════════════════════════════
                12. RACCOURCIS CLAVIER
            ══════════════════════════════════════ */}
            <SectionTitle id="shortcuts" icon={<Keyboard />} onCopy={copyLink} copied={copiedId === 'shortcuts'}>
              {t('documentation.section_shortcuts')}
            </SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('documentation.shortcutsNote')}
            </Typography>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
              {SHORTCUTS.map(({ keys, tk }, i) => (
                <React.Fragment key={tk}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5 }}>
                    <Typography variant="body2">{t(`documentation.${tk}`)}</Typography>
                    <Box>
                      {keys.map((k, j) => (
                        <React.Fragment key={j}>
                          <KeyBadge>{k}</KeyBadge>
                          {j < keys.length - 1 && (
                            <Typography component="span" variant="caption" sx={{ mx: 0.25, color: 'text.secondary' }}>+</Typography>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                  {i < SHORTCUTS.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Paper>

            {/* ══════════════════════════════════════
                13. CHANGELOG (feature #2)
            ══════════════════════════════════════ */}
            <SectionTitle id="changelog" icon={<NewReleases />} onCopy={copyLink} copied={copiedId === 'changelog'}>
              {t('documentation.section_changelog')}
            </SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.changelogIntro')}
            </Typography>
            {CHANGELOG.map(({ version, date, badge, tk }, i) => (
              <Box key={version} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {/* Ligne verticale timeline */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: badge ? 'primary.main' : 'action.hover', color: badge ? 'primary.contrastText' : 'text.secondary', fontSize: '0.7rem', fontWeight: 'bold' }}>
                    {version.replace('v', '')}
                  </Avatar>
                  {i < CHANGELOG.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 1, mb: 0 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">{version}</Typography>
                    <Typography variant="caption" color="text.secondary">— {date}</Typography>
                    {badge && <Chip label={t('documentation.changelogNew')} size="small" color="primary" />}
                  </Box>
                  <List dense disablePadding>
                    {t(`documentation.${tk}`).split(' · ').map((f, j) => (
                      <ListItem key={j} sx={{ px: 0, py: 0.1 }}>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <CheckCircle sx={{ fontSize: 12 }} color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            ))}

            {/* ══════════════════════════════════════
                14. INTÉGRATION IA (MCP — Claude Code & Codex)
            ══════════════════════════════════════ */}
            <SectionTitle id="claude" icon={<Terminal />} onCopy={copyLink} copied={copiedId === 'claude'}>
              {t('documentation.section_claude')}
            </SectionTitle>
            <Alert severity="info" icon={<Terminal />} sx={{ mb: 3 }}>
              <strong>{t('documentation.claudeAlert')}</strong>
            </Alert>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('documentation.claudeIntro')}
            </Typography>

            {/* Étapes d'installation */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[1,2,3].map(n => (
                <Grid item xs={12} sm={6} key={n}>
                  <StepCard number={n} title={t(`documentation.claudeStep${n}Title`)} description={t(`documentation.claudeStep${n}Desc`)} />
                </Grid>
              ))}
            </Grid>

            {/* Créer un token */}
            <FeatureRow
              icon={<Security />}
              title={t('documentation.claudeTokenTitle')}
              description={t('documentation.claudeTokenDesc')}
            />

            {/* ── Config Claude Code (SSE) ── */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {t('documentation.claudeConfigTitle')}
            </Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 1, fontFamily: 'monospace', fontSize: '0.78rem', overflowX: 'auto', whiteSpace: 'pre' }}>
{`{
  "mcpServers": {
    "era-project": {
      "type": "sse",
      "url": "https://app-era.groupeoctal.com/mcp/sse",
      "headers": {
        "Authorization": "Bearer era_votre_token_ici"
      }
    }
  }
}`}
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
              {t('documentation.claudeConfigDesc')}
            </Typography>

            {/* ── Config Codex (Streamable HTTP) ── */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {t('documentation.codexConfigTitle')}
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('documentation.codexAlert')}
            </Alert>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, mb: 1, fontFamily: 'monospace', fontSize: '0.78rem', overflowX: 'auto', whiteSpace: 'pre' }}>
{`{
  "mcpServers": {
    "era-project": {
      "type": "http",
      "url": "https://app-era.groupeoctal.com/mcp",
      "headers": {
        "Authorization": "Bearer era_votre_token_ici"
      }
    }
  }
}`}
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('documentation.codexConfigDesc')}
            </Typography>
            <Grid container spacing={1} sx={{ mb: 3 }}>
              {[
                { label: 'POST /mcp',         desc: t('documentation.codexEndpoint1') },
                { label: 'GET /mcp/health',   desc: t('documentation.codexEndpoint2') },
                { label: 'GET /mcp/version',  desc: t('documentation.codexEndpoint3') },
                { label: 'GET /mcp/sse',      desc: t('documentation.codexEndpoint4') },
              ].map(({ label, desc }) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="success.main" display="block" sx={{ fontFamily: 'monospace' }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Outils disponibles */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
              {t('documentation.claudeToolsTitle')}
            </Typography>
            <Grid container spacing={1} sx={{ mb: 3 }}>
              {[
                { cat: 'claudeToolsCat1', tools: 'era_get_profile, era_get_dashboard' },
                { cat: 'claudeToolsCat2', tools: 'era_list_projects, era_create_project, era_update_project, era_delete_project, era_get_project_stats, era_list_attachments…' },
                { cat: 'claudeToolsCat3', tools: 'era_list_tasks, era_create_task, era_update_task, era_update_task_status, era_create_subtask, era_update_subtask_status, era_add_task_dependency, era_remove_task_dependency…' },
                { cat: 'claudeToolsCat4', tools: 'era_list_milestones, era_create_milestone, era_list_sprints, era_create_sprint, era_start_sprint, era_complete_sprint, era_get_sprint_burndown, era_move_task_to_sprint…' },
                { cat: 'claudeToolsCat5', tools: 'era_get_workload, era_rebalance_workload, era_apply_rebalance, era_get_budget, era_update_budget_settings, era_update_hourly_rates, era_create_expense, era_update_expense, era_delete_expense…' },
                { cat: 'claudeToolsCat6', tools: 'era_start_timer, era_stop_timer, era_get_active_timer, era_log_time, era_get_time_report, era_get_analytics, era_get_calendar' },
                { cat: 'claudeToolsCat7', tools: 'era_list_messages, era_send_message, era_reply_to_message, era_invite_member, era_remove_member, era_update_member_role, era_get_notifications, era_mark_notifications_read' },
                { cat: 'claudeToolsCat8', tools: 'era_generate_report, era_list_reports, era_get_report, era_save_report, era_delete_report, era_analyze_project, era_suggest_priorities' },
              ].map(({ cat, tools }) => (
                <Grid item xs={12} sm={6} key={cat}>
                  <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                    <Typography variant="caption" fontWeight="bold" color="primary.main" display="block" gutterBottom>
                      {t(`documentation.${cat}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {tools}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Exemples */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
              {t('documentation.claudeExamplesTitle')}
            </Typography>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
              {['claudeEx1','claudeEx2','claudeEx3','claudeEx4','claudeEx5','claudeEx6'].map((key, i, arr) => (
                <React.Fragment key={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5 }}>
                    <Code fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>« {t(`documentation.${key}`)} »</Typography>
                  </Box>
                  {i < arr.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Paper>

            {/* Sécurité */}
            <Alert severity="warning" icon={<Security />} sx={{ mb: 2 }}>
              <strong>{t('documentation.claudeSecurityTitle')} : </strong>{t('documentation.claudeSecurityDesc')}
            </Alert>

            {/* ══════════════════════════════════════
                15. FAQ (feature #7 : évaluation)
            ══════════════════════════════════════ */}
            <SectionTitle id="faq" icon={<HelpOutline />} onCopy={copyLink} copied={copiedId === 'faq'}>
              {t('documentation.section_faq')}
            </SectionTitle>
            {search && filteredFaq.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('documentation.faqNoResult', { query: search })}{' '}
                <Button size="small" onClick={() => navigate('/helpdesk')}>{t('documentation.contactSupport')}</Button>
              </Alert>
            )}
            {filteredFaq.map(key => (
              <Accordion key={key} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body1" fontWeight="500">{t(`documentation.${key}Q`)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t(`documentation.${key}A`)}
                  </Typography>
                  {/* Évaluation FAQ (feature #7) */}
                  {faqRatings[key] ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {faqRatings[key] === 'up' ? <ThumbUp fontSize="small" color="success" /> : <ThumbDown fontSize="small" color="error" />}
                      <Typography variant="caption" color="text.secondary">{t('documentation.rateThanks')}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('documentation.rateQuestion')}</Typography>
                      <Tooltip title={t('documentation.rateYes')}>
                        <IconButton size="small" onClick={() => rateFaq(key, 'up')} color="success">
                          <ThumbUp fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('documentation.rateNo')}>
                        <IconButton size="small" onClick={() => rateFaq(key, 'down')} color="error">
                          <ThumbDown fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}

            {/* ── Footer ── */}
            <Divider sx={{ mt: 6, mb: 3 }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: 'center', pb: 4 }}>
              <Typography variant="body2" color="text.secondary">{t('documentation.footer')}</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" onClick={() => navigate('/helpdesk')} startIcon={<BugReport />}>
                  {t('documentation.reportIssue')}
                </Button>
                <Button variant="contained" size="small" onClick={() => navigate('/dashboard')}>
                  {t('documentation.backDashboard')}
                </Button>
              </Stack>
            </Box>

          </Grid>
        </Grid>
      </Container>
    </>
  );
}
